import { TransactionFull } from '../../models';
import {
  mapEmployeeToTopalParty,
  formatIban,
  ReimbursementInput,
} from './topal-employee-mapper';
import {
  mapTransactionsToInvoices,
} from './topal-invoice-mapper';
import {
  mapInvoiceGroupToPostings,
} from './topal-posting-mapper';
import { buildTopalXml } from './topal-xml-builder';
import { TopalValidationError } from './topal-validation-error';

export type { ReimbursementInput } from './topal-employee-mapper';
export { TopalValidationError } from './topal-validation-error';

export interface TopalExportInput {
  transactions: TransactionFull[];
  exportDate: Date;
  baseCurrency: string;
  sammelkontoCode: string;
  employeeReimbursements: Map<string, ReimbursementInput>;
}

interface EmployeeValidationError {
  employeeName: string;
  missingFields: string[];
  noReimbursementData: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  receiverIban: 'IBAN',
  receiverStreet: 'Strasse',
  receiverHouseNumber: 'Hausnummer',
  receiverCity: 'Ort',
  receiverPostalCode: 'PLZ',
};

function validateEmployeeData(
  invoiceGroups: ReturnType<typeof mapTransactionsToInvoices>,
  employeeReimbursements: Map<string, ReimbursementInput>,
): void {
  const errors: EmployeeValidationError[] = [];

  for (const group of invoiceGroups) {
    const user = group.transactions[0]?.user;
    if (!user) continue;

    const employeeName = `${user.lastName ?? ''} ${(user.firstName ?? '').substring(0, 1)}.`;
    const reimbursement = employeeReimbursements.get(user.id);

    if (!reimbursement) {
      errors.push({ employeeName, missingFields: [], noReimbursementData: true });
      continue;
    }

    const missingFields: string[] = [];

    // IBAN validation: format check after trimming and formatting
    const trimmedIban = (reimbursement.receiverIban || '').trim();
    if (!trimmedIban) {
      missingFields.push('receiverIban');
    } else {
      const formatted = formatIban(trimmedIban);
      if (!/^[A-Z]{2}\d{2}/.test(formatted) || formatted.length < 15) {
        missingFields.push('receiverIban');
      }
    }

    // Address field validation (receiverCountry NOT validated per D-03)
    const addressFields: (keyof ReimbursementInput)[] = [
      'receiverStreet',
      'receiverHouseNumber',
      'receiverCity',
      'receiverPostalCode',
    ];
    for (const field of addressFields) {
      const value = (reimbursement[field] || '').toString().trim();
      if (!value) {
        missingFields.push(field);
      }
    }

    if (missingFields.length > 0) {
      errors.push({ employeeName, missingFields, noReimbursementData: false });
    }
  }

  if (errors.length === 0) return;

  const maxDisplayed = 10;
  const lines: string[] = ['Fehlende Daten:'];

  const displayed = errors.slice(0, maxDisplayed);
  for (const error of displayed) {
    if (error.noReimbursementData) {
      lines.push(`• ${error.employeeName}: Keine Rückerstattungsdaten hinterlegt`);
    } else {
      const labels = error.missingFields.map((field) => FIELD_LABELS[field] || field);
      const verb = labels.length === 1 ? 'fehlt' : 'fehlen';
      lines.push(`• ${error.employeeName}: ${labels.join(', ')} ${verb}`);
    }
  }

  if (errors.length > maxDisplayed) {
    const remaining = errors.length - maxDisplayed;
    lines.push(`...und ${remaining} weitere Mitarbeitende`);
  }

  lines.push('Bitte ergänzen Sie die Daten in den Mitarbeitereinstellungen.');
  lines.push('Mitarbeiter müssen IBAN/Adresse in der Mobile App eingeben.');

  throw new TopalValidationError(lines.join('\n'));
}

export function generateTopalXml(input: TopalExportInput): string {
  const invoiceGroups = mapTransactionsToInvoices(
    input.transactions,
    input.exportDate,
    input.baseCurrency,
  );

  validateEmployeeData(invoiceGroups, input.employeeReimbursements);

  const employees = invoiceGroups.map((group) => {
    const user = group.transactions[0]?.user;
    if (!user) {
      throw new Error('InvoiceGroup has no transactions');
    }

    const reimbursement = input.employeeReimbursements.get(user.id)!;

    const { party, creditor, payMethod } = mapEmployeeToTopalParty(
      {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      },
      reimbursement,
    );

    // Wire sammelkontoCode into PayMethod.accountCode
    payMethod.accountCode = input.sammelkontoCode;

    const postingGroup = mapInvoiceGroupToPostings(
      group,
      input.sammelkontoCode,
      input.baseCurrency,
    );

    return {
      party,
      creditor,
      payMethod,
      invoice: group.invoice,
      transaction: postingGroup.transaction,
      postings: postingGroup.postings,
    };
  });

  return buildTopalXml({ employees });
}
